import {sprinkles} from '#style/sprinkles';

import {CTACardProps, GetCurrentColSpanArgs} from './CTACard.types';

export const getColorsForVariant = (variant: CTACardProps['variant']) => {
  switch (variant) {
    case 'inverse':
      return {
        button: 'primary-dark',
        headline: 'deepBurgundy',
        icon: 'cotton',
        lede: 'black',
      } as const;
    case 'secondary':
      return {
        button: 'primary',
        headline: 'deepBurgundy',
        icon: 'eggplantDark',
        lede: 'mineShaft',
      } as const;
    case 'primary':
    default:
      return {
        button: 'primary',
        headline: 'ecruWhite',
        icon: 'eggplant',
        lede: 'cotton',
      } as const;
  }
};

export const getCurrentColSpan = ({columnSpan = 4, isMobile, isTablet}: GetCurrentColSpanArgs) => {
  let breakpointColSpan = 4;

  if (typeof columnSpan === 'number') {
    breakpointColSpan = columnSpan;
  } else if (Array.isArray(columnSpan)) {
    if (isMobile) {
      breakpointColSpan = columnSpan[0];
    } else if (isTablet) {
      breakpointColSpan = columnSpan[1];
    } else {
      breakpointColSpan = columnSpan[columnSpan.length - 1];
    }
    // Asserting as `any` in below checks to avoid type errors. `columnSpan` is
    // typed to match the allowed values of the`gridColumnSpan` sprinkles
    // property. Sprinkles allows declaration of conditional values like
    // `{medium: 8, large: 4}`, but typescript has difficulty inferring that.
  } else if (typeof (columnSpan as any)?.small === 'number') {
    breakpointColSpan = (columnSpan as any).small;
  } else if ((isTablet || !isMobile) && typeof (columnSpan as any)?.medium === 'number') {
    breakpointColSpan = (columnSpan as any).medium;
  } else if (!isTablet && !isMobile && typeof (columnSpan as any)?.large === 'number') {
    breakpointColSpan = (columnSpan as any).large;
  }

  return breakpointColSpan;
};

export const getClassNamesForBreakpoint = ({
  columnSpan = 4,
  isMobile,
  isTablet,
}: GetCurrentColSpanArgs) => {
  const colSpan = getCurrentColSpan({columnSpan, isMobile, isTablet});

  if (colSpan === 12) {
    return {
      smallButton: sprinkles({display: 'none'}),
      largeButton: sprinkles({display: 'flex', colStart: 12}),
      layout: sprinkles({
        cols: 12,
        content: 'end',
        display: 'grid',
        gap: 24,
        h: 'full',
        items: 'start',
        p: [24, 24, 48],
        pos: 'relative',
      }),
    };
  } else {
    return {
      smallButton: sprinkles({display: 'flex'}),
      largeButton: sprinkles({display: 'none'}),
      layout: sprinkles({
        content: 'end',
        dir: 'col',
        flex: 'flex',
        gap: [16, 32],
        h: 'full',
        items: 'stretch',
        p: [24, 24, 48],
        pos: 'relative',
      }),
    };
  }
};

export const getTextVariantsForBreakpoint = ({
  columnSpan = 4,
  isMobile = false,
  isTablet = false,
}: GetCurrentColSpanArgs) => {
  // Assign uniform text variants regardless of column span on mobile.
  if (isMobile) {
    return {
      headlineVariant: 'heading2',
      ledeVariant: 'body',
      flexDirection: 'col',
      gap: 16,
    } as const;
  }

  return getCurrentColSpan({columnSpan, isMobile, isTablet}) <= 4
    ? ({headlineVariant: 'heading3', ledeVariant: 'body'} as const)
    : ({headlineVariant: 'heading2', ledeVariant: 'bodyLarge'} as const);
};
